import { FC, useEffect, useState } from 'react';

import { useCentralizedProviderData } from '@crehana/auth';
import { CREHANA_BRANDING } from '@crehana/constants/branding';
import CrehanaLogoIso from '@crehana/icons/CrehanaLogoIso';
import { useOrganizationBranding } from '@crehana/layouts/organization-branding-provider';
import { CustomNextImage as Image } from '@crehana/next';
import { IconButton } from '@crehana/ui';
import Skeleton from '@crehana/ui/dist/Skeleton';
import { getImgxUrl } from '@crehana/utils';

import PoweredBy from '@/shared/components/PoweredBy';

import { GlobalStyles } from './B2BOrgLayout.styles';
import { B2BOrgLayoutProps } from './types';

const B2BOrgLayout: FC<React.PropsWithChildren<B2BOrgLayoutProps>> = ({
  orgInfo,
  withoutLogo,
  withFooter = false,
  onBack,
  children,
}) => {
  const { heroUrl, logoUrl, loadingBrandingData } = useGetOrganizationBranding({
    learningLogoUrl: orgInfo.logoThumbnail,
    learningHeroUrl: orgInfo.loginImage,
  });

  return (
    <div className="relative w-full h-full bg-white">
      <GlobalStyles
        primaryColor={orgInfo.lookAndFeel?.primary?.main || undefined}
      />

      <div className="relative flex flex-wrap items-start justify-center w-full min-h-screen tw-pt-4.2 md:pt-32 sm:pt-64 md:pb-20">
        <section className="cui-container">
          <section className="tw-container tw-grid-cols-1 sm:tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-2 w-full max-w-full">
            <div className="hidden md:flex lg:flex flex-col justify-start">
              <div
                className="flex justify-start h-full"
                css={{ maxWidth: 580 }}
              >
                {loadingBrandingData ? (
                  <Skeleton
                    css={{ width: 529, height: 580 }}
                    isAnimated={true}
                    className="tw-rounded-1"
                  />
                ) : (
                  <Image
                    className="tw-rounded-1"
                    src={heroUrl}
                    alt="default_login_image"
                    width={529}
                    height={580}
                    objectFit="contain"
                  />
                )}
              </div>
              <PoweredBy />
            </div>
            <div className="md:tw-p-0.8">
              <div className="relative">
                {onBack && (
                  <IconButton
                    className="absolute left-0 -ml-72 hidden sm:block"
                    arrow="left"
                    onClick={onBack}
                  />
                )}
                <div className="mb-56 md:m-0">
                  {!withoutLogo && (
                    <div className="tw-flex tw-items-center tw-justify-center md:tw-block tw-mt-7.2 tw-mb-3.2 sm:mb-56 md:mt-0 md:mb-16">
                      {logoUrl ? (
                        // legacy code, will be fixed in a future rewrite
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          className="tw-h-4.4"
                          alt={orgInfo.name}
                          src={getImgxUrl(logoUrl, {
                            auto: 'format',
                            h: 56,
                            w: 'auto',
                            dpr: 2,
                          })}
                        />
                      ) : (
                        <CrehanaLogoIso
                          size={18}
                          ignoreColor
                          className="fill-current text-primary-main"
                        />
                      )}
                    </div>
                  )}

                  <div className="bg-white md:bg-transparent md:py-36 px-0 rounded-10">
                    {children}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </section>

        {/* Footer */}
        {withFooter && (
          <div className="tw-flex md:tw-hidden tw-justify-center tw-items-center tw-mt-auto tw-w-full tw-h-7.2 tw-bg-neutral-light-200 tw-text-neutral-light-500 tw-py-1.6 tw-gap-0.8">
            <span className="tw-font-body5">Powered by</span>
            <CrehanaLogoIso size={18} ignoreColor className="fill-current" />
          </div>
        )}
      </div>
    </div>
  );
};

type TUseGetOrganizationBrandingOptions = {
  learningLogoUrl?: string | null;
  learningHeroUrl?: string | null;
};

const useGetOrganizationBranding = ({
  learningLogoUrl,
  learningHeroUrl,
}: TUseGetOrganizationBrandingOptions) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<{
    logoUrl?: string | null;
    heroUrl?: string | null;
  }>();

  const { currentPageOrganization, loading: loadingCentralizedProviderData } =
    useCentralizedProviderData();
  const { branding } = useOrganizationBranding();

  useEffect(() => {
    if (loadingCentralizedProviderData) {
      return;
    }

    if (currentPageOrganization?.id) {
      setData({
        logoUrl: branding.imageLogo.absoluteUrl,
        heroUrl: branding.imagePageLoginHero.absoluteUrl,
      });
      setLoading(false);
      return;
    }

    // solo learning organizations
    setData({
      logoUrl: learningLogoUrl,
      heroUrl: learningHeroUrl
        ? `${learningHeroUrl}?w=564&h=580&auto=format&fit=contain`
        : null,
    });
    setLoading(false);
  }, [
    loadingCentralizedProviderData,
    branding,
    currentPageOrganization,
    learningLogoUrl,
    learningHeroUrl,
  ]);

  return {
    loadingBrandingData: loading,
    logoUrl: data?.logoUrl || CREHANA_BRANDING.imageLogo.absoluteUrl,
    heroUrl: data?.heroUrl || CREHANA_BRANDING.imagePageLoginHero.absoluteUrl,
  };
};

export default B2BOrgLayout;
